import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '@/utils/animations';
import ProjectCard from '@/components/ui/ProjectCard';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { projectsData } from '@/data';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState(projectsData);
  const { ref, isInView } = useInView(0.05, { rootMargin: '0px 0px 15% 0px' });

  // Define categories once
  const categories = [
    { value: 'all', label: 'All Projects' },
    { value: 'embedded', label: 'Embedded' },
    { value: 'firmware', label: 'Firmware' },
    { value: 'hardware', label: 'Hardware' },
    { value: 'web', label: 'Web Development' },
    { value: 'robotics', label: 'Robotics' }
  ];

  // Filter projects when category changes
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredProjects(projectsData);
    } else {
      setFilteredProjects(
        projectsData.filter(project => project.category === selectedCategory)
      );
    }
  }, [selectedCategory]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="projects" className="min-w-0 py-24 bg-secondary/30" ref={ref as React.RefObject<HTMLDivElement>}>
      <div className="container min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            My <span className="text-primary">Projects</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
          A collection of projects spanning embedded systems, software development, and hardware design.
          </p>
        </motion.div>

        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-12 min-w-0">
          <div className="flex max-w-full min-w-0 justify-start overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch] md:justify-center">
            <TabsList className="inline-flex w-max max-w-none shrink-0 flex-nowrap bg-background/80 backdrop-blur-sm md:flex-wrap md:justify-center">
              {categories.map(category => (
                <TabsTrigger
                  key={category.value}
                  value={category.value}
                >
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value={selectedCategory} forceMount>
            <motion.div
              className="grid min-w-0 grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {filteredProjects.map(project => (
                <motion.div key={project.id} variants={itemVariants} className="min-w-0">
                  <ProjectCard
                    id={project.id}
                    title={project.title}
                    description={project.description}
                    image={project.image}
                    tags={project.tags}
                    githubUrl={project.githubUrl}
                    liveUrl={project.liveUrl}
                    featured={project.featured}
                    completionDate={project.completionDate}
                    contextLabel={project.contextLabel}
                  />
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>
        </Tabs>

        <div className="text-center">
          <Button variant="outline" asChild>
            <a href="https://github.com/Jenaro98" target="_blank" rel="noopener noreferrer">
              View More on GitHub
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
